<template>
  <section class="w-100 px-3 pt-0 mt-0">
    <MenuItem
      class="menu-item"
      :text="t('CL_HELP_PHONE_SETTINGS')"
      v-if="linkSettingsAvailable"
      @click="$emit('linkSettings')"
      :border="false"
      icon-padding="pe-0"
    />
    <hr class="w-100 p-0 m-0" />
    <MenuItem
      :text="t('EMERGENCYCALL')"
      @click="makeCall"
      :border="false"
      icon-padding="pe-0"
    />
    <hr class="w-100 p-0 m-0" />
    <MenuItem
      :text="t('WARRANTY_SUPPORT.SUPPORT')"
      @click="setShowWarrantyModal(true)"
      :border="false"
      icon-padding="pe-0"
    />
  </section>
  <FullScreenModal
    v-model:is-open="showWarrantyModal"
    @on-close="setShowWarrantyModal(false)"
  >
    <div class="d-flex flex-column justify-content-center px-3 h-100">
      <div class="w-100 mx-auto img-container">
        <img
          :src="useAsset('img/credit-line/warranty.svg')"
          alt="warranty"
          height="166"
          class="w-100"
        />
      </div>

      <div class="px-3">
        <h1 class="text-center mt-3">
          {{ t("WARRANTY_SUPPORT.MODAL_TITLE") }}
        </h1>
        <p class="text-center mt-3">
          {{ t("WARRANTY_SUPPORT.MODAL_DESCRIPTION") }}
        </p>
      </div>

      <footer
        class="mt-auto mb-3 position-absolute bottom-0 start-0 end-0 mx-auto"
      >
        <Button @click="applyWarranty">
          <span>{{ t("WARRANTY_SUPPORT.APPLY") }}</span>
        </Button>
        <Button secondary class="mt-1" @click="setShowWarrantyModal(false)">
          <span>{{ t("CLOSE") }}</span>
        </Button>
      </footer>
    </div>
  </FullScreenModal>

  <!--///////WARRANTY RESULT///////-->
  <div
    class="d-block px-3 d-flex flex-row justify-content-start position-absolute start-0 end-0 mx-auto"
    :class="{
      error: !successfullyWarrantyApplied,
      success: successfullyWarrantyApplied,
    }"
    id="warranty-result"
    v-if="showWarrantyResult"
  >
    <img
      :src="warrantyResultIcon"
      alt="result icon"
      height="23"
      width="23"
      class="my-auto ms-0 me-1"
    />
    <div>
      {{
        t(
          successfullyWarrantyApplied
            ? "WARRANTY_SUPPORT.SUCCESS_RESULT"
            : "WARRANTY_SUPPORT.FAIL_RESULT"
        )
      }}
    </div>
  </div>
</template>

<script lang="ts" setup>
import MenuItem from "@/apps/credit-line/components/Help/MenuItem.vue";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import Swal from "sweetalert2";
import { AndroidInterface } from "@/apps/credit-line/services/android.service";
import { ref, computed } from "vue";
import FullScreenModal from "@/apps/credit-line/components/FullScreenModal.vue";
import Button from "@/apps/credit-line/components/Button.vue";
import useAsset from "@/apps/credit-line/helpers/useAsset";

declare const Android: AndroidInterface;

defineProps<{
  linkSettingsAvailable: boolean;
}>();

defineEmits<{
  (e: "back"): void;
  (e: "linkSettings"): void;
}>();

const { t } = useI18n();

const showWarrantyModal = ref<boolean>(false);
const showWarrantyResult = ref<boolean>(false);
const successfullyWarrantyApplied = ref<boolean>(false);
const warrantyResultIcon = computed(() => {
  if (successfullyWarrantyApplied.value) {
    return useAsset("img/credit-line/check_box.png");
  } else {
    return useAsset("img/credit-line/emergency_home.png");
  }
});

const makeCall = async () => {
  const result = await Swal.fire({
    title: t("PHONETOCALL"),
    input: "number",
    validationMessage: t("ENTERVALIDNUMBER"),
  });

  if (result.value && !isNaN(result.value)) {
    const validNumber = Android.isValidEmergencyNumber(result.value);
    if (validNumber) {
      console.log("valid number");
      window.open(`tel:${result.value}`, "_system");
    }
  }
};

// Exposing the callback function for the removability request
window.uninstallCallback = (reason: string) => {
  successfullyWarrantyApplied.value = reason === "DEVICE_REMOVABLE";
  showWarrantyResult.value = true;
};

const setShowWarrantyModal = (val: boolean) => (showWarrantyModal.value = val);

const applyWarranty = () => {
  try {
    Android.requestRemovabilityStatus();
  } catch (error) {
    console.log(error);
    showWarrantyResult.value = true;
    successfullyWarrantyApplied.value = false;
  }
  setShowWarrantyModal(false);
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";
section {
  margin-top: 2rem;
}

.menu-item {
  cursor: pointer;
  min-height: 3.5rem;
}

h1 {
  color: black;
  text-align: center;
  font-feature-settings: "liga" off, "clig" off;
  font-family: Inter, serif;
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
}

p {
  color: #595a5a;
  text-align: center;
  font-feature-settings: "liga" off, "clig" off;
  font-family: Inter, serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
}

.text-section {
  s & p {
    color: var(--Typography-Text-2-ICON, #666);

    /* Text regular-content-Page title */
    font-size: 0.875rem;
    font-style: normal;
    font-weight: 600;
    line-height: 1.625rem; /* 185.714% */
  }

  & p + p {
    color: var(--Typography-Text-2-ICON, #666);
    font-feature-settings: "clig" off, "liga" off;

    /* Text lighter */
    font-size: 0.875rem;
    font-style: normal;
    font-weight: 400;
    line-height: 1.25rem; /* 142.857% */
  }
}

#warranty-result {
  border-radius: 5px;
  padding: 18px 15px;
  width: 90%;
  margin-top: 100%;
}

.error {
  background: #ffe4e6;
}

.success {
  background: #e6f4ea;
}

footer {
  width: 90%;
}

.img-container {
  margin-top: -60%;
}

Button span {
  font-family: Inter, serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
}
</style>
